import { EyeOutlined } from '@ant-design/icons';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { notificationController } from '@app/controllers/notificationController';
import { Pagination } from '@app/models/Pagination';
import { ExportExtensionTableRow } from '@app/models/export_extention/ExportExtentionResponse';
import { TablePaginationConfig, Space, Tooltip, Button } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const initialPagination: Pagination = {
  current: 1,
  pageSize: 5,
};

interface ExportExtentionTableProps {
  export_id: number | null;
  editable?: boolean;
  status: string | null;
  extensionData: ExportExtensionTableRow[];
}

const ExportExtentionTable: React.FC<ExportExtentionTableProps> = ({ export_id, extensionData }) => {
  const [tableData, setTableData] = useState<{
    data: ExportExtensionTableRow[];
    pagination: Pagination;
    loading: boolean;
  }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });

  const navigate = useNavigate();

  const prepareData = useCallback(() => {
    try {
      const filteredData = export_id ? extensionData.filter((item) => item.export_id === export_id) : extensionData;

      setTableData({
        data: filteredData,
        pagination: {
          current: 1,
          pageSize: initialPagination.pageSize,
          total: filteredData.length,
        },
        loading: false,
      });
    } catch (error: any) {
      setTableData({
        data: [],
        pagination: initialPagination,
        loading: false,
      });

      notificationController.error({
        message: error.message,
      });
    }
  }, [extensionData, export_id]);

  useEffect(() => {
    prepareData();
  }, [prepareData]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setTableData((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        current: pagination.current || 1,
        pageSize: pagination.pageSize || 5,
      },
    }));
  };

  const handleViewRow = (record: ExportExtensionTableRow) => {
    navigate(`/view-export-extension`, {
      state: { id: record.id, export_id: record.export_id },
    });
  };

  const showTotal = (total: number, range: [number, number]): React.ReactNode => {
    return (
      <span style={{ fontSize: '14px', marginRight: '20px', color: '#adadad' }}>
        {`Showing ${range[0]} - ${range[1]} of ${total} Extensions`}
      </span>
    );
  };

  const columns: ColumnsType<ExportExtensionTableRow> = [
    {
      title: 'Export ID',
      dataIndex: 'export_id',
      key: 'export_id',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Request Date',
      dataIndex: 'request_date',
      key: 'request_date',
      render: (text: string) => {
        if (!text) return <span>N/A</span>;

        const date = new Date(text);

        const formatted = date.toLocaleString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });

        return <span>{formatted}</span>;
      },
    },
    {
      title: 'Expiration Countdown',
      dataIndex: 'expire_date',
      key: 'expire_date',
      render: (text: string) => {
        if (!text) return <span>N/A</span>;

        const expirationDate = new Date(text);
        const now = new Date();
        const diffTime = expirationDate.getTime() - now.getTime();
        const daysLeft = Math.ceil(diffTime / (1000 * 3600 * 24));

        const isExpiringSoon = daysLeft <= 7;

        return <span style={{ color: isExpiringSoon ? 'red' : 'inherit' }}>{daysLeft} Days Remaining</span>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        return (
          <Space>
            <Tooltip title="View">
              <Button
                type="text"
                onClick={() => handleViewRow(record)}
                icon={<EyeOutlined />}
                style={{ color: '#1890ff' }}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <BaseCard id="exp-extensions-table" padding="1.25rem">
      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: tableData.pagination.current,
          pageSize: tableData.pagination.pageSize,
          total: tableData.pagination.total,
          showTotal,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: true, scrollToFirstRowOnChange: true }}
        locale={{
          emptyText: 'No export extensions for this import.',
        }}
      />
    </BaseCard>
  );
};

export default ExportExtentionTable;
